import './styles/app.scss'

import { useUserStore } from '@/context/useUserStore'
import Hotjar from '@hotjar/browser'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { MotionConfig } from 'framer-motion'
import { QueryClientProvider } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import { Toaster } from './components/ui/toaster'
import NetworkStatus from './components/upseat-ui/app/networkStatus'
import { PageAnnouncer } from './components/upseat-ui/app/PageAnnouncer'
import ProtectedRoutes from './components/upseat-ui/app/protectedRoutes'
import PublicRoutes from './components/upseat-ui/app/publicRoutes'
import { LoadingScreen } from './components/upseat-ui/loaders/LoadingScreen'
import { useAppStore } from './context/useAppStore'
import SentryInitialization from './helpers/sentry'
import { useVersionChecker } from './hooks/app/useVersionChecker'
import { toast } from './hooks/notifications/useToast'
import {
  hasCookieConsent,
  updateAnalyticsConsent,
} from './libs/analytics/analytics'
import ClaimTicket from './pages/claimTicket'

import { SendEmail } from './helpers/email/sendEmail'
import useEnvironmentSupport from './hooks/app/useEnvironmentSupport'
import { queryClient } from './libs/queryClient'
import { cleanTokenFromLocalStorage } from './libs/utils/client'
import { tokenStore } from './services/tokenStore'

import { useInAppBrowserRedirect } from './hooks/app/useInAppBrowserRedirect'
import { usePageVisibility } from './hooks/contentChannel/usePageVisibility'
import { WALLET_QUERY_KEY } from './hooks/wallet/useWalletQuery'
import { MaintenancePage } from './pages/error'
import { DeviceNotSupported } from './pages/error/deviceNotSupported'
import { apiClient } from './services/axios'
import { getMemories } from './services/Memories/getMemories'
import { getUser } from './services/User/getUser'
import { getTickets } from './services/Wallet/getTickets'

function formatUser(res: any) {
  return {
    uid: res.uid,
    username: res.email.split('@')[0],
    firstname: res.first_name,
    lastname: res.last_name,
    email: res.email,
    phoneNumber: res.phone_number,
    cookies: res.accepted_cookies,
    marketing: res.accepted_marketing,
    isSuperUser: !!res.is_superuser,
    preferences_required:
      res.accepted_cookies === null || res.accepted_cookies === undefined,
  }
}

const BOOT_REFRESH_DELAY_MS = 500

function App() {
  useEnvironmentSupport()
  const location = useLocation()
  const { isWhatsApp } = useInAppBrowserRedirect()
  const { user, setUser } = useUserStore()
  const { errors, setAppStore } = useAppStore()
  const [authAttempted, setAuthAttempted] = useState(false)
  const [authInProgress, setAuthInProgress] = useState(false)
  const [smallDevice, setSmallDevice] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)
  const [maintenance] = useState(false) // Set to true to enable maintenance mode
  const hotjarInitialized = useRef(false)
  const [searchParams] = useSearchParams()

  // admin portal preview mode flag
  const previewMode =
    useMemo(() => searchParams.get('preview'), [searchParams]) === 'true'

  const fnbOrder = searchParams.get('fnbOrder')

  useEffect(() => {
    if (fnbOrder) {
      localStorage.setItem('fnbOrder', fnbOrder)
    }
  }, [fnbOrder])

  usePageVisibility(
    () => {
      // pause all HTML5 videos on the page
      document.querySelectorAll('video').forEach((v) => v.pause())
    },
    () => {
      // optionally resume — or leave it for user to manually resume
    },
  )

  // Listen for logout message from parent domain (live preview iframe)
  useEffect(() => {
    const portalOrigin =
      process.env.REACT_APP_ENVIRONMENT === 'production'
        ? 'https://portal.upapp.uk'
        : 'https://dev-portal.upapp.uk'
    const handler = (event: MessageEvent) => {
      if (event.origin !== portalOrigin) return
      if (event.data === 'logout') {
        tokenStore.clear()
        localStorage.removeItem('user')
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  useEffect(() => {
    setAuthChecked(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check for new version
  useVersionChecker()

  // GA boots in index.tsx (Consent Mode, default denied). Keep gtag's consent
  // state in sync with the user's stored choice and, once granted, store the
  // GA client id for backend session correlation.
  useEffect(() => {
    updateAnalyticsConsent(hasCookieConsent())
    if (!hasCookieConsent()) return

    const timer = setTimeout(() => {
      if (window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID) {
        window.gtag(
          'get',
          process.env.REACT_APP_GA_MEASUREMENT_ID,
          'client_id',
          (cid: string) => {
            if (cid) {
              localStorage.setItem('ga_client_id', cid)
            }
          },
        )
      }
    }, 800)

    return () => clearTimeout(timer)
  }, [user?.cookies])

  // Track page views on route change. Not consent-gated: Consent Mode decides
  // whether the hit carries identifiers or goes out as a cookieless ping.
  useEffect(() => {
    if (!window.gtag) return

    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    })
  }, [location])

  // Detect small devices
  useEffect(() => {
    const mq = window.matchMedia('(max-device-width: 300px)')
    const check = () => setSmallDevice(mq.matches)

    check()
    mq.addEventListener('change', check)

    return () => mq.removeEventListener('change', check)
  }, [])

  // On boot: attempt silent refresh via cookie, then fetch user
  useEffect(() => {
    if (localStorage.getItem('iframe_mode') === 'true') return
    if (authAttempted || authInProgress) return

    setAuthAttempted(true)
    setAuthInProgress(true)

    if (!navigator.onLine) {
      setAuthInProgress(false)
      setAppStore({ loading: { app: false, initial: false } })
      return
    }

    // In-app browsers can hand their cookie store to the network process after
    // the first request goes out, so the refresh cookie misses it. Beat delay.
    new Promise((resolve) => setTimeout(resolve, BOOT_REFRESH_DELAY_MS))
      .then(() => apiClient.post('upauth/session/refresh'))
      .then((res) => {
        tokenStore.set(res.data.access)
        return getUser()
      })
      .then((res) => {
        if (res) {
          setUser(formatUser(res))
          queryClient.prefetchQuery({
            queryKey: WALLET_QUERY_KEY,
            queryFn: () =>
              Promise.all([getTickets(), getMemories()]).then(
                ([walletRes, memories]) => ({ walletRes, memories }),
              ),
          })
        } else {
          cleanTokenFromLocalStorage()
        }
      })
      .catch(() => {
        if (
          process.env.REACT_APP_ENVIRONMENT === 'development' &&
          tokenStore.get()
        )
          return
        cleanTokenFromLocalStorage()
      })
      .finally(() => {
        setAuthInProgress(false)
        setAppStore({ loading: { app: false, initial: false } })
      })
  }, [authAttempted, authInProgress, setUser, setAppStore])

  const showErrorToast = useCallback((title: string, description: string) => {
    toast({ title, description, variant: 'error' })
  }, [])

  useEffect(() => {
    if (errors.server) {
      showErrorToast(
        'Server Error',
        'Something went wrong, please try again later',
      )
    }
    if (errors.network) {
      showErrorToast('Network Error', 'It looks like you are offline')
    }
  }, [errors.server, errors.network, showErrorToast])

  // Initialize Hotjar
  useEffect(() => {
    if (user?.cookies && !hotjarInitialized.current) {
      hotjarInitialized.current = true
      Hotjar.init(parseInt(process.env.REACT_APP_HOTJAR_ID as string), 6, {
        debug: process.env.REACT_APP_ENVIRONMENT !== 'production',
      })

      const appVersion = localStorage.getItem('app-version')

      Hotjar.identify(user.uid as string, {
        firstname: user.firstname as string,
        lastname: user.lastname as string,
        email: user.email,
        appVersion: appVersion || '',
      })
    }

    if (hotjarInitialized.current) {
      Hotjar.stateChange(location.pathname)
    }
  }, [location, user])

  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <div data-testid="up-app" id="up-app">
          {maintenance ? (
            <MaintenancePage />
          ) : (
            <>
              <NetworkStatus />
              <PageAnnouncer />
              <Toaster />
              {!smallDevice || previewMode ? (
                <div
                  className={`upseat w-screen bg-purple flex ${
                    isWhatsApp ? 'min-h-[100svh]' : 'h-[100dvh] overflow-hidden'
                  }`}
                >
                  <GoogleOAuthProvider
                    clientId={
                      process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID as string
                    }
                  >
                    <SentryInitialization user={user} />

                    {!authChecked || authInProgress ? (
                      <LoadingScreen />
                    ) : (
                      <Routes>
                        <Route path="/share/:uid" element={<ClaimTicket />} />
                        <Route
                          path="/*"
                          element={
                            user ? <ProtectedRoutes /> : <PublicRoutes />
                          }
                        />
                      </Routes>
                    )}
                  </GoogleOAuthProvider>
                </div>
              ) : (
                <DeviceNotSupported sendEmail={SendEmail} />
              )}
            </>
          )}
        </div>
      </MotionConfig>
    </QueryClientProvider>
  )
}

export default App
